import React, { useState, useEffect } from "react";

const API = process.env.REACT_APP_API_URL || "";

export default function ZoomCookies() {
  const [accounts, setAccounts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [busy, setBusy]           = useState(false);
  const [paste, setPaste]         = useState("");
  const [label, setLabel]         = useState("");
  const [toast, setToast]         = useState(null);

  const token = () => localStorage.getItem("token") || "";

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const r = await fetch(`${API}/tencent/zoom-accounts`, {
        headers: { Authorization: "Bearer " + token() }
      });
      const d = await r.json();
      if (Array.isArray(d)) setAccounts(d);
    } catch {
      showToast("Load failed", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(true);
    // in_use / free state changes as bots claim and release accounts
    const t = setInterval(() => load(false), 10000);
    return () => clearInterval(t);
  }, []);

  const postCookies = async (blob, filename, lbl) => {
    const fd = new FormData();
    fd.append("file", blob, filename);
    fd.append("label", lbl);
    const r = await fetch(`${API}/tencent/zoom-accounts/upload`, {
      method: "POST",
      headers: { Authorization: "Bearer " + token() },
      body: fd,
    });
    const d = await r.json();
    return { ok: r.ok && d.id, data: d };
  };

  const handleUpload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (const file of Array.from(files)) {
      try {
        const res = await postCookies(file, file.name, file.name.replace(/\.json$/i, ""));
        if (res.ok) ok++;
        else { fail++; showToast(res.data.detail || `${file.name} failed`, "error"); }
      } catch { fail++; }
    }
    setBusy(false);
    if (ok) showToast(`${ok} account${ok === 1 ? "" : "s"} added`);
    else if (fail) showToast("Upload failed", "error");
    load();
  };

  const handlePaste = async () => {
    const text = paste.trim();
    if (!text) return;
    // Validate in the browser so a bad paste gives an obvious message
    // instead of a generic 400 from the server.
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      showToast("Valid JSON nahi hai — poora array copy karo", "error");
      return;
    }
    if (!Array.isArray(parsed) || !parsed.length) {
      showToast("JSON ek non-empty cookie array hona chahiye", "error");
      return;
    }
    const domains = parsed.map(c => String(c.domain || "")).join(" ");
    if (!domains.includes("zoom.us")) {
      showToast("Ye zoom.us ki cookies nahi lag rahi — zoom.us pe login karke export karo", "warn");
    }
    setBusy(true);
    try {
      const blob = new Blob([text], { type: "application/json" });
      const res = await postCookies(blob, "pasted.json",
        label.trim() || `pasted-${new Date().toISOString().slice(11, 19)}`);
      if (res.ok) {
        showToast(`Added — ${res.data.cookie_count} cookies`);
        setPaste(""); setLabel("");
        load();
      } else {
        showToast(res.data.detail || "Add failed", "error");
      }
    } catch {
      showToast("Add failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API}/tencent/zoom-accounts/${id}`, {
        method: "DELETE",
        headers: { Authorization: "Bearer " + token() }
      });
      showToast("Account removed");
      load();
    } catch {
      showToast("Delete failed", "error");
    }
  };

  const activeCount = accounts.reduce((n, a) => n + (a.active_uses || 0), 0);
  const liveSets    = accounts.filter(a => a.status !== "disabled").length;

  const fmtTime = (ts) => {
    if (!ts) return "—";
    try { return new Date(ts * 1000).toLocaleString(); } catch { return "—"; }
  };

  // Zoom rotates these roughly every 24h (and __cf_bm, the Cloudflare
  // bot-management token, goes stale far sooner) — so surface the age loudly
  // rather than letting a stale set silently look like a code problem.
  const ageInfo = (uploadedAt) => {
    if (!uploadedAt) return { text: "—", stale: false };
    const hrs = (Date.now() / 1000 - uploadedAt) / 3600;
    const stale = hrs >= 24;
    if (hrs < 1)  return { text: `${Math.max(1, Math.round(hrs * 60))}m old`, stale };
    if (hrs < 48) return { text: `${Math.round(hrs)}h old`, stale };
    return { text: `${Math.round(hrs / 24)}d old`, stale };
  };

  const staleCount = accounts.filter(a => ageInfo(a.uploaded_at).stale).length;

  return (
    <div style={{ animation: "fadeIn .2s ease" }}>
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 16, zIndex: 9999,
          background: toast.type === "error" ? "rgba(127,29,29,0.97)" : toast.type === "warn" ? "rgba(120,53,15,0.97)" : "rgba(20,83,45,0.97)",
          border: `1px solid ${toast.type === "error" ? "#ef4444" : toast.type === "warn" ? "#f59e0b" : "#22c55e"}`,
          color: "#fff", borderRadius: 12, padding: "12px 20px", boxShadow: "0 4px 24px #0009",
          fontSize: 14, fontWeight: 600, backdropFilter: "blur(16px)", maxWidth: "calc(100vw - 32px)"
        }}>{toast.msg}</div>
      )}

      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>Zoom Cookie Accounts</h2>
        <p style={{ color: "#4a6280", fontSize: 13, margin: "4px 0 0" }}>
          Real logged-in Zoom sessions — bots inhe use karke guest ke bajaye signed-in user ki tarah join karte hain
        </p>
      </div>

      {/* Stats */}
      <div className="mini-stat-row" style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <div className="mini-stat">
          <div className="mini-stat-val" style={{ color: "#4ade80" }}>{liveSets}</div>
          <div className="mini-stat-lbl">Cookie Sets</div>
        </div>
        <div className="mini-stat">
          <div className="mini-stat-val" style={{ color: "#f59e0b" }}>{activeCount}</div>
          <div className="mini-stat-lbl">Bots Using Now</div>
        </div>
        <div className="mini-stat">
          <div className="mini-stat-val" style={{ color: staleCount ? "#f87171" : "#38bdf8" }}>{staleCount}</div>
          <div className="mini-stat-lbl">Stale (24h+)</div>
        </div>
      </div>

      {/* How-to */}
      <div className="adm-card adm-card-pad" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", marginBottom: 10, textTransform: "uppercase", letterSpacing: "1px" }}>Cookies kaise nikale</div>
        <ol style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.9, margin: 0, paddingLeft: 20 }}>
          <li><strong style={{ color: "#e2e8f0" }}>zoom.us</strong> pe login karo (zoom.com nahi)</li>
          <li>Chrome extension (Cookie-Editor / EditThisCookie) → Export JSON</li>
          <li>Neeche paste karo ya file upload karo</li>
        </ol>
        <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 9, fontSize: 12, color: "#fbbf24", lineHeight: 1.6 }}>
          <strong>Har ~24 ghante mein refresh karo</strong> — Zoom cookies rotate karta hai. Set ki age list mein dikhti hai; 24h paar hone pe <span style={{ color: "#f87171", fontWeight: 700 }}>laal</span> ho jayegi.
        </div>
      </div>

      {/* Add */}
      <div className="adm-card adm-card-pad" style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: "1px" }}>Add Account</div>
          <label className="adm-btn adm-btn-primary" style={{ cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1, margin: 0 }}>
            {busy ? "Working..." : "+ Upload JSON File(s)"}
            <input
              type="file" accept=".json,application/json" multiple hidden disabled={busy}
              onChange={e => { handleUpload(e.target.files); e.target.value = ""; }}
            />
          </label>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 700 }}>Ya seedha paste karo</span>
          <input
            className="count-field"
            style={{ width: 200 }}
            type="text"
            placeholder="Label (optional)"
            value={label}
            disabled={busy}
            onChange={e => setLabel(e.target.value)}
          />
          <button
            className="adm-btn adm-btn-primary adm-btn-sm"
            onClick={handlePaste}
            disabled={busy || !paste.trim()}
            style={{ marginLeft: "auto" }}
          >
            {busy ? "Adding..." : "Add Pasted Cookies"}
          </button>
        </div>
        <textarea
          value={paste}
          disabled={busy}
          onChange={e => setPaste(e.target.value)}
          placeholder='[{"domain":".zoom.us","name":"_zm_ssid","value":"...", ...}]'
          spellCheck={false}
          style={{
            width: "100%", minHeight: 140, resize: "vertical",
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 10, padding: "10px 13px", color: "#e2e8f0",
            fontFamily: "'JetBrains Mono', monospace", fontSize: 12, lineHeight: 1.5, outline: "none",
          }}
        />
        {paste.trim() && (
          <div style={{ fontSize: 11, color: "#4a6280", marginTop: 6 }}>
            {paste.trim().length.toLocaleString()} characters
          </div>
        )}
      </div>

      {/* List */}
      <div className="adm-card adm-card-pad">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: "1px" }}>Accounts</div>
          <button className="adm-btn adm-btn-sm" onClick={() => load(true)}>Refresh</button>
        </div>

        {loading ? (
          <div style={{ fontSize: 13, color: "#4a6280" }}>Loading…</div>
        ) : accounts.length === 0 ? (
          <div style={{ fontSize: 13, color: "#4a6280", lineHeight: 1.7 }}>
            Koi account add nahi hua — sabhi bots anonymous guest ke roop mein join karenge.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {accounts.map(a => (
              <div key={a.id} style={{
                display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 10, padding: "11px 15px"
              }}>
                <span style={{
                  width: 8, height: 8, borderRadius: 99, flexShrink: 0,
                  background: a.status === "disabled" ? "#64748b" : (a.active_uses > 0 ? "#f59e0b" : "#4ade80")
                }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0", minWidth: 120 }}>{a.label}</span>
                <span style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase",
                  color: a.status === "disabled" ? "#64748b" : (a.active_uses > 0 ? "#f59e0b" : "#4ade80")
                }}>
                  {a.status === "disabled"
                    ? "disabled"
                    : (a.active_uses > 0 ? `${a.active_uses} bot${a.active_uses === 1 ? "" : "s"} using` : "idle")}
                </span>
                <span style={{ fontSize: 11, color: "#4a6280" }}>{a.use_count} joins total</span>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  color: ageInfo(a.uploaded_at).stale ? "#f87171" : "#334155"
                }}>
                  {ageInfo(a.uploaded_at).text}{ageInfo(a.uploaded_at).stale ? " · refresh karo" : ""}
                </span>
                <button
                  className="adm-btn adm-btn-danger adm-btn-sm"
                  style={{ marginLeft: "auto" }}
                  onClick={() => handleDelete(a.id)}
                >Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
